/**
 * Local ability cooldown / cast tracker. Optimistic — server doesn't
 * push cooldown state to clients, so we record locally when an ability
 * fires and time it down via setInterval.
 *
 * Used by the action bar (sweep overlay), the cast bar component, and
 * the keyboard handler that prevents re-firing during cooldown.
 *
 * R232q5ay: добавлен Global Cooldown (GCD) — WotLK 3.3.5a parity.
 * GCD = 1.5s по умолчанию, urезается haste (cap floor = 1.0s по wotlk
 * правилу). instants (windup=0) и rogue energy-abilities тоже триггерят
 * GCD. Без GCD юзер мог spam abilities любым кликом — Sirus parity-fail.
 */

import { create } from 'zustand';

export const GCD_DEFAULT_MS = 1500; // Vanilla / WotLK 1.5s base GCD.
export const GCD_FLOOR_MS = 1000;   // 1.0s — hard floor at haste cap.

interface CdState {
  /** Map abilityId -> performance.now() ms when cooldown expires. */
  cooldowns: Record<string, number>;
  /** Currently casting ability — null when idle. */
  activeCast: { id: string; name: string; startedAt: number; durationMs: number } | null;
  /** R232q5h T2.1: short pulse frame for instant-cast abilities (windup=0).
   *  CastBar renders a 250ms ABI-NAME flash so the player gets feedback. */
  instantPulse: { id: string; name: string; firedAt: number } | null;
  /** R232q5ay: timestamp (Date.now ms) when GCD lockout ends. 0 = no GCD active. */
  globalCooldownEndsAt: number;
  startCooldown: (id: string, durationMs: number) => void;
  startCast: (id: string, name: string, durationMs: number) => void;
  pulseInstant: (id: string, name: string) => void;
  clearCast: () => void;
  /** Returns ms remaining (>=0) or 0 if ready. */
  remaining: (id: string) => number;
  /** R232q5ay: trigger global cooldown. `hastePct` 0..60 reduces duration,
   *  floored at GCD_FLOOR_MS. Pass 0 to use raw default. */
  triggerGcd: (hastePct?: number) => void;
  /** Returns ms remaining on GCD, 0 if free. */
  gcdRemaining: () => number;
}

export const useAbilityCD = create<CdState>((set, get) => ({
  cooldowns: {},
  activeCast: null,
  instantPulse: null,
  globalCooldownEndsAt: 0,
  startCooldown: (id, durationMs) =>
    set((s) => ({ cooldowns: { ...s.cooldowns, [id]: performance.now() + durationMs } })),
  startCast: (id, name, durationMs) =>
    set({ activeCast: { id, name, startedAt: performance.now(), durationMs } }),
  pulseInstant: (id, name) => {
    set({ instantPulse: { id, name, firedAt: performance.now() } });
    setTimeout(() => {
      const cur = get().instantPulse;
      if (cur && cur.id === id && cur.firedAt) set({ instantPulse: null });
    }, 280);
  },
  clearCast: () => set({ activeCast: null }),
  remaining: (id) => {
    const at = get().cooldowns[id];
    if (!at) return 0;
    return Math.max(0, at - performance.now());
  },
  triggerGcd: (hastePct = 0) => {
    const haste = Math.max(0, Math.min(60, hastePct));
    const dur = Math.max(GCD_FLOOR_MS, Math.floor(GCD_DEFAULT_MS * (1 - haste / 100)));
    set({ globalCooldownEndsAt: Date.now() + dur });
  },
  gcdRemaining: () => {
    const at = get().globalCooldownEndsAt;
    if (!at) return 0;
    return Math.max(0, at - Date.now());
  },
}));
